import { Component, OnDestroy, OnInit } from '@angular/core'
import { Subject } from 'rxjs'
import { filter, flatMap, take, takeUntil } from 'rxjs/operators'
import { fadeIn } from '../../../animations/fadeIn'
import { KeySystemNavConfig } from '../../../configuration/key.system.nav.config'
import { KeySystem, Structure, StructureDetails } from '../../../models/dev.test.models'
import { ErrorService } from '../../../services/error.service'
import { KeyingAuthService } from '../../../services/keying.auth.service'
import { KeyingDataSetsService, StructureDataSets } from '../../../services/keying.data.sets.service'
import { KeyingStructureService } from '../../../services/keying.structure.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { LoadingService } from '../../../services/loading.service'
import { User } from '@overtur-monorepo/keying-user'
import { MasterKeyUpdateService } from '../../../services/masterkey.update.service'
import { GenerateTmkDialogComponent } from '../../../dialogs/generate-tmk-dialog/generate-tmk-dialog.component'
import { CrossKeyDialogComponent } from '../../../dialogs/cross-key-dialog/cross-key-dialog.component'
import { AddSelectiveMasterDialogComponent } from '../../../dialogs/add-selective-master-dialog/add-selective-master-dialog.component'
import { DialogService } from '../../../services/dialog.service'
import { ThruNotationDialogComponent } from '../../../dialogs/thru-notation-dialog/thru-notation-dialog.component'
import { ConstructionKeyType, KeyNodeTitle } from '@overtur-monorepo/keying-shared'
import { ChangeLogConfirmationDialogComponent } from '../../../components/matrix/change-log-confirmation-dialog/change-log-confirmation-dialog.component'
import { AppRoutingConfig } from '../../../configuration/app.routing.config'
import { Router } from '@angular/router'
import { AddKeyRequestDto } from '../../../models/requestDto.models'
import { MoveKeysDialogComponent } from '../../../dialogs/move-keys-dialog/move-keys-dialog.component'
import { RenameKeysDialogComponent } from '../../../dialogs/rename-keys-dialog/rename-keys-dialog.component'
import { ChangeKeyTypeDialogComponent } from '../../../dialogs/change-key-type-dialog/change-key-type-dialog.component'
import { environment } from '../../../../environments/environment'

@Component({
  selector: 'app-budget',
  templateUrl: './budget.component.html',
  styleUrls: ['./budget.component.scss'],
  animations: [fadeIn],
  standalone: false,
})
export class BudgetComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  constructionKeyType = ConstructionKeyType

  structure: Structure
  keySystem: KeySystem
  keyways
  user: User
  newNotecontent
  notes = []
  loading
  hideSystemGenerateTMK = environment.flags.hideTMKGenerate

  constructor(
    private keyingAuthService: KeyingAuthService,
    private keyingStructureService: KeyingStructureService,
    private keyingSystemService: KeyingSystemService,
    private errorService: ErrorService,
    private keySystemNavConfig: KeySystemNavConfig,
    private keyingDataSetsService: KeyingDataSetsService,
    private masterKeyUpdateService: MasterKeyUpdateService,
    private loadingService: LoadingService,
    private dialogService: DialogService,
    private router: Router
  ) {}

  /* istanbul ignore next */
  isNonNull<T>(value: T): value is NonNullable<T> {
    return value != null
  }

  /* istanbul ignore next */
  ngOnInit(): void {
    this.keyingDataSetsService.structureDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe((details: StructureDetails) => {
        if (details.mecsInProgress || details.publishingInProgress || details?.pinningGenerationInprogress) {
          this.router.navigate([AppRoutingConfig.structuresUrl])
        }
      })

    this.keyingStructureService.structure$
      .pipe(
        filter(this.isNonNull),
        takeUntil(this.destroy$),
        flatMap((structure: Structure) => {
          this.structure = structure
          return this.keyingSystemService.keySystem$
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (keySystem: KeySystem) => {
          this.keySystem = keySystem

          if (this.structure && this.keySystem) {
            this.keySystemNavConfig.setNav('Budget', this.structure, this.keySystem)
          }
        },
        (error) => {
          this.errorService.throw(error)
        }
      )

    // Subscribe to User
    this.keyingAuthService.keyingUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (!user) {
        return
      }
      this.user = user
    })

    this.keyingDataSetsService.structureDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((structureDataSets: StructureDataSets) => {
        if (!structureDataSets) {
          return
        }
        this.keyways = structureDataSets.keyways
      })

    this.loadingService.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.loading = loading
    })
  }

  deleteItem(masterKey) {
    if (masterKey.thruKeys) {
      masterKey.thruKeys.forEach((thru) => {
        this.masterKeyUpdateService.deleteMasterKey(this.structure, this.keySystem, thru)
      })
    } else {
      if (!masterKey.isFutureKey)
        this.errorService.showError('Can not delete keys either interfaced or created from hierarchy')
      else this.masterKeyUpdateService.deleteMasterKey(this.structure, this.keySystem, masterKey)
    }
  }

  /* istanbul ignore next */
  patchItem(patchData) {
    if (patchData.masterKey.isPublished || patchData.masterKey.isLegacyPublished) {
      const data = {
        cancel: 'Cancel',
        confirm: 'Confirm',
        showCancel: true,
        actionType: 'KeyWay Updated',
        reasonRequired: false,
        keySystemId: this.keySystem.id,
        source: patchData.masterKey.keySetSymbol,
      }
      data.reasonRequired = true
      const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, data, false)

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirmed) {
          this.updatePatchItem(patchData, result.auditTrailRequest)
        }
      })
    } else {
      this.updatePatchItem(patchData)
    }
  }

  updatePatchItem(patchData, auditTrailRequest: any = undefined) {
    let selectedMasterKey = null
    this.masterKeyUpdateService.patchItem(
      this.structure,
      this.keySystem,
      patchData,
      selectedMasterKey,
      auditTrailRequest
    )
  }

  /* istanbul ignore next */
  addChild(data) {
    data.structure = this.structure
    data.keySystem = this.keySystem
    switch (data.action) {
      case 'cross':
        this.openDialog(CrossKeyDialogComponent, data, true)
        break
      case 'SLM':
        this.openDialog(AddSelectiveMasterDialogComponent, data, true)
        break
      case 'CHANGE_KEYS':
      case 'MASTER_KEYS':
        this.openDialog(ThruNotationDialogComponent, data, true)
        break
      case 'MOVE_KEYS':
        this.openDialog(MoveKeysDialogComponent, data, true)
        break
      case 'RENAME_KEYS':
        this.openDialog(RenameKeysDialogComponent, data, true)
        break
      case 'CHANGE_KEY_TYPE':
        this.openDialog(ChangeKeyTypeDialogComponent, data, true)
        break
      default:
        this.masterKeyUpdateService.addChild(this.structure, this.keySystem, data).subscribe()
        break
    }
  }

  createTmk() {
    if (this.canGenerateTmk()) {
      this.keyingDataSetsService.keySystemDataSets$.pipe(take(1)).subscribe((data) => {
        if (!data) {
          return
        } else if (
          this.keySystem.constructionTypeId === this.constructionKeyType.RCKChanges ||
          this.keySystem.constructionTypeId === this.constructionKeyType.RCKNoMaster ||
          this.keySystem.constructionTypeId === this.constructionKeyType.RCKTMK
        ) {
          this.openDialog(GenerateTmkDialogComponent, {
            structure: this.structure,
            keySystem: this.keySystem,
            isRCK: true,
          })
        } else if (data.keySystem) {
          this.openDialog(GenerateTmkDialogComponent, {
            structure: this.structure,
            keySystem: this.keySystem,
            isRCK: false,
          })
          return
        }
        //  else if (data.keySystem && !data.keySystem.topMasterKey) {
        //   // return this.generateTmk({})
        // }
      })
    } else {
      return this.errorService.throw({ error: { message: 'Please choose KeySection for TMK.' } })
    }
  }

  canGenerateTmk() {
    let canGenerate = false
    canGenerate = this.keySystem.masterKeysBudget.squashIncludeChangeKeys().some((mk) => !mk.parentId && mk.keywayId)
    return canGenerate
  }

  /* istanbul ignore next */
  openDialog(dialog, data?, autoFocus = false): void {
    let dialogRef = this.dialogService.open(dialog, data, autoFocus)

    dialogRef.afterClosed().subscribe((request) => {
      if (request) {
        switch (dialog) {
          case CrossKeyDialogComponent:
            this.masterKeyUpdateService.addChild(this.structure, this.keySystem, request).subscribe()
            break
          case ThruNotationDialogComponent:
            if (
              request.action === KeyNodeTitle.CHANGE_KEYS ||
              request.action === KeyNodeTitle.MASTER_KEYS ||
              request.action === KeyNodeTitle.MOVE_KEYS
            ) {
              request.isFutureKey = true
              this.masterKeyUpdateService.addChild(this.structure, this.keySystem, request).subscribe()
            } else if (request.action === KeyNodeTitle.DELETE_KEYS) {
              this.masterKeyUpdateService.deleteChildren(this.structure, this.keySystem, request)
            }
            break
          case AddSelectiveMasterDialogComponent:
            request.isFutureKey = true
            this.masterKeyUpdateService.addChild(this.structure, this.keySystem, request).subscribe()
            break
          default:
            break
        }
      }
    })
  }

  /* istanbul ignore next */
  generateTmk(request, generate = true) {
    if (!this.canGenerateTmk()) {
      return this.errorService.throw({ error: { message: 'Please choose KeySection for TMK.' } })
    }

    this.keyingSystemService
      .generateTmk(this.structure.id, this.keySystem.id, request)
      .pipe(take(1))
      .subscribe(
        () => {
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          if (generate) {
            this.openDialog(GenerateTmkDialogComponent, {
              structure: this.structure,
              keySystem: this.keySystem,
              isRCK: false,
            })
          }
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }
}
